import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, Save, Building2, Phone, Mail, MapPin, FileText,
  FlaskConical, Scan, Receipt, Loader2, CheckCircle2, X, Image, CalendarClock,
} from 'lucide-react';
import { clsx } from 'clsx';
import { brandingApi, settingsApi } from '@/lib/api';
import { Branding } from '@/hooks/useBranding';
import { useAuthStore } from '@/store/auth.store';

// ── Shared helpers ─────────────────────────────────────────────────

function Field({
  label, hint, value, onChange, type = 'text', rows,
}: {
  label: string; hint?: string; value: string;
  onChange: (v: string) => void; type?: string; rows?: number;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
      {rows ? (
        <textarea
          rows={rows}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy
                     focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40
                     resize-y placeholder:text-gray-300"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy
                     focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy/40
                     placeholder:text-gray-300"
        />
      )}
    </div>
  );
}

function SectionCard({
  icon: Icon, title, children, onSave, saving, saved,
}: {
  icon: React.ElementType; title: string; children: React.ReactNode;
  onSave: () => void; saving: boolean; saved: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-navy/8 flex items-center justify-center">
            <Icon size={15} className="text-navy"/>
          </div>
          <h2 className="font-semibold text-navy text-sm">{title}</h2>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-orange text-gray-900
                     text-xs font-semibold hover:bg-orange-dark disabled:opacity-50 transition shadow-sm">
          {saving ? (
            <Loader2 size={12} className="animate-spin"/>
          ) : saved ? (
            <CheckCircle2 size={12} className="text-green-700"/>
          ) : (
            <Save size={12}/>
          )}
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );
}

// ── Logo uploader ──────────────────────────────────────────────────
function LogoSection({ logo, onLogoSaved }: { logo: string; onLogoSaved: (url: string) => void }) {
  const [preview,  setPreview]  = useState<string>(logo);
  const [uploading, setUploading] = useState(false);
  const [error,    setError]    = useState('');
  const [saved,    setSaved]    = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setPreview(logo); }, [logo]);

  function handleFile(file: File) {
    setError('');
    setSaved(false);
    if (!['image/png','image/jpeg','image/svg+xml','image/webp'].includes(file.type)) {
      setError('Only PNG, JPEG, SVG, or WebP allowed.');
      return;
    }
    if (file.size > 500_000) {
      setError('Logo must be under 500 KB. Resize the image and try again.');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  async function handleUpload() {
    if (preview === logo) return;
    setUploading(true);
    setError('');
    try {
      if (preview) {
        await brandingApi.uploadLogo(preview);
      } else {
        // Saving an empty value clears the logo
        await brandingApi.update({ branding_logo: '' });
      }
      onLogoSaved(preview);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Upload failed. Please try again.');
    } finally { setUploading(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-navy/8 flex items-center justify-center">
            <Image size={15} className="text-navy"/>
          </div>
          <div>
            <h2 className="font-semibold text-navy text-sm">Hospital Logo</h2>
            <p className="text-xs text-gray-400">Appears on prescriptions, invoices & reports</p>
          </div>
        </div>
        <button
          onClick={handleUpload}
          disabled={uploading || preview === logo}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-orange text-gray-900
                     text-xs font-semibold hover:bg-orange-dark disabled:opacity-50 transition shadow-sm">
          {uploading ? <Loader2 size={12} className="animate-spin"/> :
           saved     ? <CheckCircle2 size={12} className="text-green-700"/> : <Upload size={12}/>}
          {saved ? 'Saved' : preview !== logo ? 'Save' : 'Upload'}
        </button>
      </div>
      <div className="p-6 flex items-start gap-6 flex-wrap">
        {/* Preview */}
        <div
          onClick={() => fileRef.current?.click()}
          className={clsx(
            'w-32 h-32 rounded-xl border-2 border-dashed flex flex-col items-center justify-center',
            'cursor-pointer hover:border-orange/50 hover:bg-orange/4 transition-colors group',
            preview ? 'border-gray-300' : 'border-gray-200',
          )}>
          {preview ? (
            <img src={preview} alt="Logo" className="w-full h-full object-contain p-2 rounded-xl"/>
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-gray-400 group-hover:text-orange transition-colors">
              <Upload size={22} strokeWidth={1.5}/>
              <span className="text-xs">Click to upload</span>
            </div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="text-xs text-gray-500 space-y-1">
            <p>• Accepted formats: <strong>PNG, JPEG, SVG, WebP</strong></p>
            <p>• Maximum size: <strong>500 KB</strong></p>
            <p>• Recommended: <strong>square or landscape, at least 200×200 px</strong></p>
            <p>• Will be shown in print headers at approx. <strong>14mm × 14mm</strong></p>
          </div>
          {preview && (
            <button
              onClick={() => { setPreview(''); }}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 mt-2">
              <X size={11}/> Remove logo
            </button>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden"
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function BrandingPage() {
  const queryClient = useQueryClient();
  const user        = useAuthStore(s => s.user);

  const { data: branding, isLoading } = useQuery<Branding>({
    queryKey: ['branding'],
    queryFn:  () => brandingApi.get().then(r => r.data),
    staleTime: 5 * 60_000,
  });

  // Local editable state per section
  const [identity, setIdentity] = useState({
    branding_tagline:    '',
    branding_license_no: '',
    hospital_address:    '',
    hospital_phone:      '',
    hospital_email:      '',
  });
  const [rx,        setRx]        = useState({ branding_rx_footer: '' });
  const [invoice,   setInvoice]   = useState({ branding_invoice_footer: '' });
  const [lab,       setLab]       = useState({ branding_lab_footer: '' });
  const [radiology, setRadiology] = useState({ branding_radiology_footer: '' });

  // Seed local state when branding loads
  useEffect(() => {
    if (!branding) return;
    setIdentity({
      branding_tagline:    branding.branding_tagline    ?? '',
      branding_license_no: branding.branding_license_no ?? '',
      hospital_address:    branding.hospital_address    ?? '',
      hospital_phone:      branding.hospital_phone      ?? '',
      hospital_email:      branding.hospital_email      ?? '',
    });
    setRx       ({ branding_rx_footer:         branding.branding_rx_footer         ?? '' });
    setInvoice  ({ branding_invoice_footer:     branding.branding_invoice_footer     ?? '' });
    setLab      ({ branding_lab_footer:         branding.branding_lab_footer         ?? '' });
    setRadiology({ branding_radiology_footer:   branding.branding_radiology_footer   ?? '' });
  }, [branding]);

  // Per-section save state
  // OPD clinical settings
  const [followupDays, setFollowupDays] = useState<number>(7);

  const { data: opdSettings } = useQuery({
    queryKey: ['settings-opd'],
    queryFn:  () => settingsApi.getOpd().then(r => r.data),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (opdSettings?.free_followup_days !== undefined) {
      setFollowupDays(opdSettings.free_followup_days);
    }
  }, [opdSettings]);

  const [saving,  setSaving]  = useState<Record<string, boolean>>({});
  const [saved,   setSavedMap] = useState<Record<string, boolean>>({});

  async function save(section: string, data: object) {
    setSaving(s => ({ ...s, [section]: true }));
    try {
      await brandingApi.update(data);
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      setSavedMap(s => ({ ...s, [section]: true }));
      setTimeout(() => setSavedMap(s => ({ ...s, [section]: false })), 3000);
    } finally {
      setSaving(s => ({ ...s, [section]: false }));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 size={22} className="animate-spin"/>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

      {/* Page header */}
      <div className="mb-2">
        <h1 className="text-lg font-bold text-navy">Branding &amp; Print Settings</h1>
        <p className="text-xs text-gray-400 mt-0.5">
          Configure how your hospital identity appears on prescriptions, invoices, and lab/radiology reports.
          <span className="font-medium text-navy"> {user?.hospitalName}</span>
        </p>
      </div>

      {/* Logo */}
      <LogoSection
        logo={branding?.branding_logo ?? ''}
        onLogoSaved={() => queryClient.invalidateQueries({ queryKey: ['branding'] })}
      />

      {/* Identity & Contact */}
      <SectionCard
        icon={Building2}
        title="Hospital Identity &amp; Contact"
        onSave={() => save('identity', identity)}
        saving={saving.identity ?? false}
        saved={saved.identity  ?? false}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Tagline / Speciality"
            hint="Shown below hospital name on all prints"
            value={identity.branding_tagline}
            onChange={v => setIdentity(s => ({ ...s, branding_tagline: v }))}
          />
          <Field
            label="License / Registration No."
            hint="e.g., Pharmacy Lic. MH-1234 or AERB Reg."
            value={identity.branding_license_no}
            onChange={v => setIdentity(s => ({ ...s, branding_license_no: v }))}
          />
        </div>
        <Field
          label="Full Address"
          hint="Printed in footer of all documents"
          value={identity.hospital_address}
          onChange={v => setIdentity(s => ({ ...s, hospital_address: v }))}
          rows={2}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Phone / WhatsApp"
            value={identity.hospital_phone}
            onChange={v => setIdentity(s => ({ ...s, hospital_phone: v }))}
          />
          <Field
            label="Email"
            type="email"
            value={identity.hospital_email}
            onChange={v => setIdentity(s => ({ ...s, hospital_email: v }))}
          />
        </div>
      </SectionCard>

      {/* Prescription footer */}
      <SectionCard
        icon={FileText}
        title="Prescription Footer"
        onSave={() => save('rx', rx)}
        saving={saving.rx ?? false}
        saved={saved.rx  ?? false}>
        <Field
          label="Additional footer text"
          hint='Printed below the writing area on blank prescriptions — e.g., "Valid for 30 days · Follow-up as advised"'
          value={rx.branding_rx_footer}
          onChange={v => setRx({ branding_rx_footer: v })}
          rows={3}
        />
        <p className="text-xs text-gray-400">
          The address and phone from Hospital Identity are always shown in the prescription footer.
        </p>
      </SectionCard>

      {/* Invoice footer */}
      <SectionCard
        icon={Receipt}
        title="Invoice Footer"
        onSave={() => save('invoice', invoice)}
        saving={saving.invoice ?? false}
        saved={saved.invoice   ?? false}>
        <Field
          label="Payment &amp; Terms (shown at bottom of invoice)"
          hint="Include bank account details, UPI ID, or payment terms"
          value={invoice.branding_invoice_footer}
          onChange={v => setInvoice({ branding_invoice_footer: v })}
          rows={4}
        />
      </SectionCard>

      {/* Lab report footer */}
      <SectionCard
        icon={FlaskConical}
        title="Lab / Pathology Report Footer"
        onSave={() => save('lab', lab)}
        saving={saving.lab ?? false}
        saved={saved.lab   ?? false}>
        <Field
          label="Report footer / accreditation note"
          hint='e.g., "NABL Accredited Lab · Results reviewed by qualified pathologist · For queries contact lab@hospital.in"'
          value={lab.branding_lab_footer}
          onChange={v => setLab({ branding_lab_footer: v })}
          rows={3}
        />
      </SectionCard>

      {/* Radiology footer */}
      <SectionCard
        icon={Scan}
        title="Radiology Report Footer"
        onSave={() => save('radiology', radiology)}
        saving={saving.radiology ?? false}
        saved={saved.radiology   ?? false}>
        <Field
          label="Report footer / disclaimer"
          hint='e.g., "AERB Registered · Report by qualified radiologist · Clinical correlation advised"'
          value={radiology.branding_radiology_footer}
          onChange={v => setRadiology({ branding_radiology_footer: v })}
          rows={3}
        />
      </SectionCard>

      {/* OPD Clinical Settings */}
      <SectionCard
        icon={CalendarClock}
        title="OPD Clinical Settings"
        onSave={async () => {
          setSaving(s => ({ ...s, opd: true }));
          try {
            await settingsApi.patchOpd({ free_followup_days: followupDays });
            setSavedMap(s => ({ ...s, opd: true }));
            setTimeout(() => setSavedMap(s => ({ ...s, opd: false })), 3000);
          } finally {
            setSaving(s => ({ ...s, opd: false }));
          }
        }}
        saving={saving.opd ?? false}
        saved={saved.opd   ?? false}>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-600 block">
            Free Follow-up Window (days)
          </label>
          <p className="text-xs text-gray-400 mb-2">
            When a patient visits the same doctor within this many days of a completed encounter,
            the OPD consultation charge is automatically set to ₹0 in the draft invoice.
            Set to <strong>0</strong> to disable free follow-ups.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={followupDays}
              min={0}
              max={90}
              onChange={e => setFollowupDays(Math.min(90, Math.max(0, parseInt(e.target.value) || 0)))}
              className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy"
            />
            <span className="text-sm text-gray-500">days</span>
            {followupDays === 0 && (
              <span className="text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 px-2 py-1 rounded-lg">
                Follow-ups disabled — full fee always charged
              </span>
            )}
            {followupDays > 0 && (
              <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-lg">
                Free follow-up within {followupDays} day{followupDays !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </SectionCard>

    </div>
  );
}
